import { Badge, type DataTableColumn } from "@design";
import { useTranslation } from "react-i18next";
import type { ExtensionSummary } from "@/api";
import { Text } from "@/components/Text";
import { STATE_BADGE, stateOf, useIssueText } from "./setupStatus";

function NameCell({ extension }: Readonly<{ extension: ExtensionSummary }>) {
  const { t } = useTranslation("adminIntegrations");
  return (
    <div className="flex min-w-0 flex-col gap-1 text-left">
      <span className="flex items-center gap-2">
        <Text as="span" variant="body-sm" className="truncate">
          {extension.name}
        </Text>
        {extension.setup?.kind === "custom" && (
          <Badge size="compact" variant="neutral">
            {t("custom")}
          </Badge>
        )}
      </span>
      {extension.description && (
        <Text as="span" variant="caption" tone="tertiary" className="line-clamp-1">
          {extension.description}
        </Text>
      )}
    </div>
  );
}

function StatusCell({ extension }: Readonly<{ extension: ExtensionSummary }>) {
  const { t } = useTranslation("adminIntegrations");
  const issueText = useIssueText();
  const state = stateOf(extension);
  const first = extension.setup?.issues?.[0];
  return (
    <div className="flex min-w-0 flex-col items-end gap-1 md:items-start">
      <Badge variant={STATE_BADGE[state]}>{t(`state.${state}`)}</Badge>
      {first && (
        <Text as="span" variant="caption" tone="tertiary" className="max-w-64 truncate">
          {issueText(first)}
        </Text>
      )}
    </div>
  );
}

export function useIntegrationColumns(): DataTableColumn<ExtensionSummary>[] {
  const { t } = useTranslation("adminIntegrations");
  return [
    {
      key: "name",
      header: t("columns.name"),
      mobile: "primary",
      render: (extension) => <NameCell extension={extension} />,
    },
    { key: "status", header: t("columns.status"), render: (extension) => <StatusCell extension={extension} /> },
    {
      key: "tools",
      header: t("columns.tools"),
      render: (extension) => (
        <Text as="span" variant="body-sm" tone="secondary" className="whitespace-nowrap">
          {t("toolCount", { count: extension.toolCount ?? 0 })}
        </Text>
      ),
    },
    {
      key: "access",
      header: t("columns.access"),
      render: (extension) => (
        <Text as="span" variant="body-sm" tone="secondary" className="whitespace-nowrap">
          {extension.teamAccessCount ? t("access.teams", { count: extension.teamAccessCount }) : t("access.everyone")}
        </Text>
      ),
    },
  ];
}
